import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Reports = () => {
  const [reportData, setReportData] = useState({});
  const [selectedReport, setSelectedReport] = useState('financial');
  const [dateRange, setDateRange] = useState('month');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const generateReport = async () => {
      setLoading(true);
      try {
        switch (selectedReport) {
          case 'financial':
            await generateFinancialReport();
            break;
          case 'occupancy':
            await generateOccupancyReport();
            break;
          case 'maintenance':
            await generateMaintenanceReport();
            break;
          case 'rent_roll':
            await generateRentRollReport();
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error generating report:', error);
      }
      setLoading(false);
    };
    generateReport();
  }, [selectedReport, dateRange]);



  const generateFinancialReport = async () => {
    // Fetch financial data
    const { data: transactions } = await supabase
      .from('transactions')
      .select(`
        *,
        properties(name),
        units(unit_number)
      `)
      .order('transaction_date', { ascending: false });

    const { data: rentPayments } = await supabase
      .from('rent_payments')
      .select('*')
      .eq('status', 'paid');

    const income = transactions?.filter(t => t.transaction_type === 'credit').reduce((sum, t) => sum + parseFloat(t.amount), 0) || 0;
    const expenses = transactions?.filter(t => t.transaction_type === 'debit').reduce((sum, t) => sum + parseFloat(t.amount), 0) || 0;
    const rentIncome = rentPayments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;

    setReportData({
      totalIncome: income,
      totalExpenses: expenses,
      netIncome: income - expenses,
      rentIncome,
      transactions: transactions || [],
      rentPayments: rentPayments || []
    });
  };

  const generateOccupancyReport = async () => {
    const { data: properties } = await supabase
      .from('properties')
      .select(`
        *,
        units(*, leases(status))
      `);

    const occupancyData = properties?.map(property => {
      const totalUnits = property.units.length;
      const occupiedUnits = property.units.filter(unit => 
        unit.leases && unit.leases.some(lease => lease.status === 'active')
      ).length;
      const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

      return {
        propertyName: property.name,
        totalUnits,
        occupiedUnits,
        vacantUnits: totalUnits - occupiedUnits,
        occupancyRate
      };
    }) || [];

    setReportData({ occupancyData });
  };

  const generateMaintenanceReport = async () => {
    const { data: maintenanceRequests } = await supabase
      .from('maintenance_requests')
      .select(`
        *,
        units(unit_number, properties(name)),
        work_orders(actual_cost, status)
      `)
      .order('created_at', { ascending: false });

    const totalRequests = maintenanceRequests?.length || 0;
    const completedRequests = maintenanceRequests?.filter(r => r.status === 'completed').length || 0;
    const totalCost = maintenanceRequests?.reduce((sum, r) => {
      return sum + (r.work_orders?.reduce((orderSum, wo) => orderSum + (parseFloat(wo.actual_cost) || 0), 0) || 0);
    }, 0) || 0;

    const categoryBreakdown = maintenanceRequests?.reduce((acc, request) => {
      acc[request.category] = (acc[request.category] || 0) + 1;
      return acc;
    }, {}) || {};

    setReportData({
      totalRequests,
      completedRequests,
      totalCost,
      categoryBreakdown,
      requests: maintenanceRequests || []
    });
  };

  const generateRentRollReport = async () => {
    const { data: leases } = await supabase
      .from('leases')
      .select(`
        *,
        units(unit_number, properties(name)),
        residents(first_name, last_name, email, phone),
        rent_payments(status, due_date, paid_date)
      `)
      .eq('status', 'active')
      .order('lease_end', { ascending: true });

    const totalRent = leases?.reduce((sum, lease) => sum + parseFloat(lease.rent_amount), 0) || 0;
    const collectedRent = leases?.reduce((sum, lease) => {
      const paidPayments = lease.rent_payments?.filter(p => p.status === 'paid').length || 0;
      return sum + (paidPayments * parseFloat(lease.rent_amount));
    }, 0) || 0;

    setReportData({
      leases: leases || [],
      totalRent,
      collectedRent,
      collectionRate: totalRent > 0 ? (collectedRent / totalRent) * 100 : 0
    });
  };

  const exportReport = () => {
    // Export functionality would go here
    alert('Export functionality would be implemented here');
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2>Reports & Analytics</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button onClick={exportReport} className="btn btn-secondary">Export PDF</button>
            <button onClick={() => window.location.reload()} className="btn btn-primary">Refresh</button>
          </div>
        </div>

        {/* Report Navigation */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setSelectedReport('financial')}
            className={`btn ${selectedReport === 'financial' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Financial Report
          </button>
          <button 
            onClick={() => setSelectedReport('occupancy')}
            className={`btn ${selectedReport === 'occupancy' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Occupancy Report
          </button>
          <button 
            onClick={() => setSelectedReport('maintenance')}
            className={`btn ${selectedReport === 'maintenance' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Maintenance Report
          </button>
          <button 
            onClick={() => setSelectedReport('rent_roll')}
            className={`btn ${selectedReport === 'rent_roll' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Rent Roll
          </button>
          <button 
            onClick={() => setSelectedReport('owner_statement')}
            className={`btn ${selectedReport === 'owner_statement' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Owner Statement
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Generating report...</p>
          </div>
        )}

        {/* Financial Report */}
        {selectedReport === 'financial' && !loading && (
          <div>
            <h3>Financial Performance Report</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Income</h4>
                <p style={{ fontSize: '24px', color: '#28a745', fontWeight: 'bold' }}>
                  ${reportData.totalIncome?.toLocaleString() || '0'}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Expenses</h4>
                <p style={{ fontSize: '24px', color: '#dc3545', fontWeight: 'bold' }}>
                  ${reportData.totalExpenses?.toLocaleString() || '0'}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Net Income</h4>
                <p style={{ fontSize: '24px', color: '#007bff', fontWeight: 'bold' }}>
                  ${reportData.netIncome?.toLocaleString() || '0'}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Rent Income</h4>
                <p style={{ fontSize: '24px', color: '#17a2b8', fontWeight: 'bold' }}>
                  ${reportData.rentIncome?.toLocaleString() || '0'}
                </p>
              </div>
            </div>

            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h4>Recent Transactions</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {reportData.transactions?.slice(0, 10).map(transaction => (
                  <div key={transaction.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <div>
                      <strong>{transaction.description}</strong>
                      <p style={{ fontSize: '12px', color: '#666' }}>{transaction.properties?.name}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ color: transaction.transaction_type === 'credit' ? '#28a745' : '#dc3545' }}>
                        {transaction.transaction_type === 'credit' ? '+' : '-'}${transaction.amount}
                      </span>
                      <p style={{ fontSize: '12px', color: '#666' }}>{new Date(transaction.transaction_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Occupancy Report */}
        {selectedReport === 'occupancy' && !loading && (
          <div>
            <h3>Occupancy Report</h3>
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Property</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Total Units</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Occupied</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Vacant</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Occupancy Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.occupancyData?.map((property, index) => (
                    <tr key={index}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{property.propertyName}</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{property.totalUnits}</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{property.occupiedUnits}</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>{property.vacantUnits}</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: property.occupancyRate >= 90 ? '#d4edda' : property.occupancyRate >= 70 ? '#fff3cd' : '#f8d7da',
                          color: property.occupancyRate >= 90 ? '#155724' : property.occupancyRate >= 70 ? '#856404' : '#721c24'
                        }}>
                          {Math.round(property.occupancyRate)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Maintenance Report */}
        {selectedReport === 'maintenance' && !loading && (
          <div>
            <h3>Maintenance Report</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Requests</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>{reportData.totalRequests}</p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Completed</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{reportData.completedRequests}</p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Cost</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>${reportData.totalCost?.toLocaleString()}</p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Completion Rate</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                  {reportData.totalRequests > 0 ? Math.round((reportData.completedRequests / reportData.totalRequests) * 100) : 0}%
                </p>
              </div>
            </div>

            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h4>Requests by Category</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '15px' }}>
                {Object.entries(reportData.categoryBreakdown || {}).map(([category, count]) => (
                  <div key={category} style={{ textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <p style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{category}</p>
                    <p style={{ fontSize: '18px', color: '#007bff' }}>{count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Rent Roll Report */}
        {selectedReport === 'rent_roll' && !loading && (
          <div>
            <h3>Rent Roll Report</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Rent</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                  ${reportData.totalRent?.toLocaleString()}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Collected</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                  ${reportData.collectedRent?.toLocaleString()}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Collection Rate</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                  {Math.round(reportData.collectionRate || 0)}%
                </p>
              </div>
            </div>

            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Tenant</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Property/Unit</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Rent</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Lease End</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.leases?.map(lease => (
                    <tr key={lease.id}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {lease.residents?.first_name} {lease.residents?.last_name}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {lease.units?.properties?.name} - {lease.units?.unit_number}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        ${lease.rent_amount}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        {new Date(lease.lease_end).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: '#d4edda',
                          color: '#155724'
                        }}>
                          Active
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;